/** Local town filtering; no geolocation, network requests or map dependency. */
const storeTownKey = (town) => town.trim().replace(/\s+/g, ' ').toLocaleLowerCase();

class StoreDirectory extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.controller = new AbortController();
    this.select = this.querySelector('[data-store-select]');
    if (!this.select) return;
    this.rebuild();
    this.select.addEventListener('change', () => this.filter(this.select.value, true), { signal: this.controller.signal });
    document.addEventListener('shopify:block:select', (event) => {
      if (!(event.target instanceof Element)) return;
      const card = event.target.closest('[data-store-card]');
      if (card && this.contains(card) && card.hidden) this.filter('', true);
    }, { signal: this.controller.signal });
    // Support inserted/reordered store blocks without watching changes to our own controls.
    this.observer = new MutationObserver((records) => {
      if (records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) =>
        node instanceof Element && (node.matches('[data-store-card]') || node.querySelector('[data-store-card]'))
      ))) {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.rebuild());
      }
    });
    this.observer.observe(this, { childList: true, subtree: true });
  }

  rebuild() {
    if (!this.isConnected) return;
    this.cards = [...this.querySelectorAll('[data-store-card]')];
    const towns = new Map();
    [...this.cards].sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position)).forEach((card) => {
      const label = card.dataset.town.trim().replace(/\s+/g, ' ');
      const key = storeTownKey(label);
      if (key && !towns.has(key)) towns.set(key, label);
    });
    const sortedTowns = [...towns].sort((a, b) => a[1].localeCompare(b[1], 'en', { sensitivity: 'base' }));
    const choices = [['', this.dataset.allLabel], ...sortedTowns];
    this.select.replaceChildren(...choices.map(([key, label]) => {
      const option = document.createElement('option');
      option.value = key;
      option.textContent = label;
      return option;
    }));
    this.querySelectorAll('[data-store-controls]').forEach((control) => { control.hidden = towns.size === 0; });
    this.filter(towns.has(this.selectedTown) ? this.selectedTown : '', false);
  }

  filter(town, announce) {
    this.selectedTown = town;
    this.select.value = town;
    let count = 0;
    let ordinaryCount = 0;
    this.cards.forEach((card) => {
      card.hidden = Boolean(town && storeTownKey(card.dataset.town) !== town);
      if (!card.hidden) {
        count++;
        if (!card.hasAttribute('data-featured')) ordinaryCount++;
      }
    });
    const group = this.querySelector('[data-store-grid-group]');
    if (group) group.hidden = ordinaryCount === 0;
    const status = this.querySelector('[data-store-status]');
    if (status && announce) {
      status.textContent = (count === 1 ? this.dataset.oneResult : this.dataset.manyResults).replaceAll('[count]', String(count));
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }
}

class StoreAnchor extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.schedule = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateTarget());
    };
    this.observer?.disconnect();
    this.observer = new MutationObserver(this.schedule);
    this.observer.observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
    for (const name of ['DOMContentLoaded', 'shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
      document.addEventListener(name, this.schedule, { signal: this.controller.signal });
    }
    this.schedule();
  }

  updateTarget() {
    if (!this.isConnected) return;
    const link = this.querySelector('a');
    if (!link) return;
    const target = document.querySelector('[data-store-directory]');
    link.hidden = !target;
    if (target) link.setAttribute('href', `#${target.id}`);
    else link.removeAttribute('href');
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('bm-store-directory')) customElements.define('bm-store-directory', StoreDirectory);
if (!customElements.get('bm-store-anchor')) customElements.define('bm-store-anchor', StoreAnchor);
